import { Box, useDisclosure } from "@chakra-ui/react";
import * as React from "react";
import { Button } from "@chakra-ui/button";
import { loadStripe, StripeCardElement } from "@stripe/stripe-js";
import toast from "react-hot-toast";
import { getPlan, resumeSubscription } from "../../controllers/subscription";
import { ConfirmPopup } from "../popups/confirmPopup";
import * as analytics from "../../utils/analytics";
import * as Sentry from "@sentry/nextjs";
import { useFirebaseUser } from "../../utils/useFirebaseUser";
import { useSubscription } from "hooks/useSubscription";
import { planAndTargetUsersMapping, useCampaign } from "hooks/useCampaign";
import { getAccount } from "utils/sessionHelper";

const liveKey =
  "pk_live_51JaEQHKB4y5X3SShCqZzCQTp3Qju78mj9lapztVUYBHwC0W2nbkfDwUQfOjnwxFHHQRKrVU3oo9CC52R2mzRP4TR00Tva63lj1";
const testKey =
  "pk_test_51JaEQHKB4y5X3SShRqiyOJoet4gOItWoMpi8CLLOBtA9BtrcPsj3P5RdhTNU9GH9k7ZhegBtrhuMA8Miqw67yWgU00e2tRa7q1";

const devPlans = {
  // growMonthly: "price_1JjMnDKB4y5X3SSh7AVScCkC", // 49
  growMonthly: "price_1KRiu4KB4y5X3SShDgLhkxNJ", // 29
  // growYearly: "", //
};

const prodPlans = {
  // growMonthly: "price_1Jdxk1KB4y5X3SShvC5pCuEq", // 49
  // growMonthly: "price_1KRitQKB4y5X3SShv3V86lMh", // 29
  // growYearly: "price_1KRitQKB4y5X3SShv3V86lMh", // 29
  // startMonthly: "price_1MZWG7KB4y5X3SShRvzrSXN6", // 52 monthly - prod_NKAbUYeT4CBXu7
  // startYearly: "price_1MZWH0KB4y5X3SShp74w0ZKs", // 468 - 39 monthly - prod_NKAcCCLXdjn8ih
  // startMonthly: "price_1OeJivKB4y5X3SSh8SQ6iRwA", // 65 monthly - prod_NKAbUYeT4CBXu7
  // startYearly: "price_1OeJkEKB4y5X3SShoF7CR2pF", // 588 - 49 monthly - prod_NKAcCCLXdjn8ih
  startMonthly: "price_1PY3sMKB4y5X3SShqQi73n9y", // 39 monthly - prod_NKAbUYeT4CBXu7 - Starter 06/2024
  startYearly: "price_1PY45mKB4y5X3SSh1Ft9CBcI", // 384 yearly - 32 monthly - prod_NKAcCCLXdjn8ih  - Starter Yearly 06/2024
  // growMonthly: "price_1KwikNKB4y5X3SShsX6aF7jB", // 39 monthly
  // growYearly: "price_1KwikNKB4y5X3SShsX6aF7jB", // 39 monthly
  // growMonthly: "price_1MTk3IKB4y5X3SShQR3FTtMH", // 52 monthly - prod_KIYrvwkEn7QXbf
  // growYearly: "price_1MTk4zKB4y5X3SShiamZDEQ7", // 468 - 39 monthly - prod_MwTvOxBEaw1CPU
  growMonthly: "price_1OeJjdKB4y5X3SShvHwgxQA9", // 65 monthly - prod_KIYrvwkEn7QXbf
  growYearly: "price_1OeJkkKB4y5X3SShWP9sB07p", // 588 - 49 monthly - prod_MwTvOxBEaw1CPU
  // enterpriseMonthly: "price_1MTk6dKB4y5X3SShLjkchBZM", // 94 monthly - prod_NECVUOpjEd3ngp
  // enterpriseYearly: "price_1MTk8DKB4y5X3SShJtU63sxu", // 852 - 71 monthly - prod_NECXvheOKxm1Yc
  enterpriseMonthly: "price_1NIS5YKB4y5X3SShF6Wh7zYg", // 199 monthly - prod_NECVUOpjEd3ngp
  enterpriseYearly: "price_1NIS6rKB4y5X3SShxHkRoqkg", // 1788 - 149 monthly - prod_NECXvheOKxm1Yc

  // growYearly: "", //
};

// let actualPlans = !process.env.NODE_ENV || process.env.NODE_ENV === "development" ? devPlans : prodPlans;
let actualPlans = prodPlans;

// let actualKey = !process.env.NODE_ENV || process.env.NODE_ENV === "development" ? testKey : liveKey;
let actualKey = liveKey;

const stripePromise = loadStripe(actualKey);

export const Payment = (props: any) => {
  const [isLoading, setLoading] = React.useState(false);
  const {
    isOpen: isOpenConfirm,
    onOpen: onOpenConfirm,
    onClose: onCloseConfirm,
  } = useDisclosure();
  const {
    isOpen: isOpenConfirmFreeTrialUpdate,
    onOpen: onOpenConfirmFreeTrialUpdate,
    onClose: onCloseConfirmFreeTrialUpdate,
  } = useDisclosure();
  const isAuthenticated = useFirebaseUser();
  const { updatePlan: updateStripePlan } = useSubscription();
  const { activeCampaign, updateUserAcquisition } = useCampaign();
  const isPayingCustomer = !getAccount(props.session)?.subStatus?.includes(
    "FreeTrial"
  );
  const isTargetedUser =
    activeCampaign?.showItTo?.some(
      (s) => planAndTargetUsersMapping[s] === getPlan(props.session?.user)
    ) || false;

  const updatePlan = async () => {
    try {
      setLoading(true);
      const isSuccess = await updateStripePlan(props.plan, props.discount);

      if (
        isSuccess &&
        props.billingFrequency === "monthly" &&
        isPayingCustomer &&
        isTargetedUser &&
        activeCampaign
      ) {
        updateUserAcquisition();
      }
      props.refresher && props.refresher({});
    } finally {
      setLoading(false);
    }
  };

  const subscribe = async (event) => {
    props.onClickStartFreeTrial && props.onClickStartFreeTrial();
    const stripe = await stripePromise;

    try {
      //@ts-ignore
      if (Rewardful) {
        var referralData = {
          //@ts-ignore
          referral: Rewardful.referral,
          //@ts-ignore
          isReferral: Rewardful.referral ? true : false,
        };
      } else {
        //@ts-ignore
        var referralData = { isReferral: false };
      }
    } catch (e) {
      //@ts-ignore
      var referralData = { isReferral: false };
      Sentry.captureException(e);
    }

    console.log(referralData);
    analytics.log("plan_selected", {
      ...referralData,
      source: props.source ? props.source : "unknown",
    });

    if (!props?.session?.user?.uid) {
      let message = "Session not initialized during payment";

      toast.error("Failed to initialized payment, please contact us", {
        style: { background: "gray.600", color: "#222" },
      });
      return;
    }

    if (!stripe) {
      // let message = "stripe not loaded in payment";
      toast.error("Failed to initialized payment, please contact us", {
        style: { background: "gray.600", color: "#222" },
      });

      return;
    }
    console.log("discount: " + props.discount);
    const subscription = props?.session?.user?.data?.subscription;

    let data = {
      isLocal: !process.env.NODE_ENV || process.env.NODE_ENV === "development",
      baseUrl: window?.location?.origin,
      priceId: actualPlans[props.plan],
      //@ts-ignore
      userId: props.session.user.uid,
      discount: props.discount,
      hasSubscribedBefore: subscription && !!Object.keys(subscription).length,
      ...referralData,
    };

    if (props.customCancelUrl)
      //@ts-ignore
      data.customCancelUrl = props.customCancelUrl;

    setLoading(true);
    const res = await fetch("/api/payment", {
      method: "POST",
      headers: new Headers({ "Content-Type": "application/json", token: "" }),
      credentials: "same-origin",
      body: JSON.stringify(data),
    });

    let stripeSession = await res.json();

    // console.log({ session: stripeSession });

    if (stripeSession && stripeSession.success) {
      let params = {
        sessionId: stripeSession.id,
      };

      const result = await stripe.redirectToCheckout(params);
      setLoading(false);

      if (result.error) {
        // If `redirectToCheckout` fails due to a browser or network
        // error, display the localized error message to your customer
        // using `result.error.message`.
        console.log("Error in payment: " + result.error.message);
      }
    } else {
      setLoading(false);
      console.log(
        "An error happened during checkout. Please contact us for more information"
      );

      toast.error("Failed to initialized payment, please contact us", {
        style: { background: "gray.600", color: "#222" },
      });
    }
  };

  const {
    color,
    customCancelUrl,
    billingFrequency,
    refresher,
    onClickStartFreeTrial,
    ...rest
  } = props;
  rest.color = props.textColor;

  // console.log("render payment");

  if (
    props?.session?.user?.data?.subscription?.isSubscribed &&
    !props?.session?.user?.data?.subscription.price
  ) {
    props.session.user.data.subscription.price =
      actualPlans["startMonthlyBeta"];
  }

  let text = props.buttonText ? props.buttonText : "Start 7-Day Free Trial";
  const buttonFontSize = props.buttonFontSize
    ? props.buttonFontSize
    : props.compact
    ? "xs"
    : "md";

  let isDisabled = false;
  if (!isAuthenticated) {
    isDisabled = true;
  } else if (
    props?.session?.user?.data?.subscription?.isPaused ||
    (props?.session?.user?.data?.subscription?.isSubscribed &&
      props.session.user.data.subStatus?.includes("_CancelRequest"))
  ) {
    text = "Resume";
  } else if (
    props?.session?.user?.data?.subscription?.isSubscribed &&
    actualPlans[props.plan] == props?.session?.user?.data?.subscription.price &&
    props?.session?.user?.data?.subscription?.subscription
  ) {
    text = "Your plan";
    isDisabled = true;
  } else if (
    props?.session?.user?.data?.subscription?.isSubscribed &&
    props?.session?.user?.data?.subscription?.subscription
  ) {
    text = "Update plan";
  } else if (
    props?.session?.user?.data?.subscription &&
    Object.keys(props?.session?.user?.data?.subscription).length
  ) {
    text = "Subscribe";
  }

  return (
    <Box>
      <Button
        w={props.buttonWidth ? props.buttonWidth : "full"}
        size={props.buttonSize ? props.buttonSize : "lg"}
        colorScheme={props.color ? props.color : undefined}
        variant={props.variant ? props.variant : undefined}
        m={props.buttonMargin ? props.buttonMargin : undefined}
        px={props.compact ? "8" : "4"}
        fontWeight={props.buttonFontWeight ? props.buttonFontWeight : "bold"}
        fontSize={buttonFontSize}
        textTransform={props.transform ? props.transform : "uppercase"}
        onClick={(e) => {
          if (
            props?.billingFrequency == "yearly" &&
            window?.location?.href?.includes("pricing")
          ) {
            onOpenConfirm();
          } else {
            if (
              props?.session?.user?.data?.subscription?.isPaused ||
              (props?.session?.user?.data?.subscription?.isSubscribed &&
                props.session.user.data.subStatus?.includes("_CancelRequest"))
            ) {
              resumeSubscription(props?.session, setLoading, refresher);
            } else if (
              props?.session?.user?.data?.subscription?.isSubscribed &&
              props?.session?.user?.data?.subscription?.subscription
            ) {
              if (
                props?.session?.user?.data?.subStatus?.includes("FreeTrial")
              ) {
                onOpenConfirmFreeTrialUpdate();
              } else {
                updatePlan();
              }
            } else {
              subscribe(e);
            }
          }
        }}
        isLoading={isLoading}
        isDisabled={isDisabled}
        // {...rest}
      >
        {text}
      </Button>
      <ConfirmPopup
        isOpen={isOpenConfirmFreeTrialUpdate}
        onClose={onCloseConfirmFreeTrialUpdate}
        title="Please confirm"
        body="Updating your plan during the free trial will stop the free trial and bill you immediately. Do you want to continue?"
        callback={() => {
          updatePlan();
        }}
      />
      <ConfirmPopup
        isOpen={isOpenConfirm}
        onClose={onCloseConfirm}
        title="Please confirm"
        body="You’re about to make savings by subscribing to a yearly subscription instead of a monthly one."
        callback={() => {
          if (props?.session?.user?.data?.subscription?.isSubscribed) {
            updatePlan();
          } else {
            subscribe(null);
          }
        }}
      />
    </Box>
  );
};

export default Payment;
